export const INCIDENT_STATUSES = ['active', 'monitoring', 'closed'] as const;

export type IncidentStatus = typeof INCIDENT_STATUSES[number];

export type ClosedReason = 'closed_by_cs' | 'checked_by_prerender' | 'account_not_paying';

export type IncidentEventActor = 'system' | 'cs';

export const INCIDENT_EVENT_TYPES = [
  'created',
  'domain_joined',
  'domain_no_longer_affected',
  'domain_affected_again',
  'status_change',
  'severity_change',
  'inconclusive',
] as const;

export type IncidentEventType = typeof INCIDENT_EVENT_TYPES[number];

export const SEVERITIES = ['critical', 'high', 'medium', 'low'] as const;

export type Severity = typeof SEVERITIES[number];

export const CRAWLER_CATEGORIES = ['search', 'ai', 'social', 'other'] as const;

export type CrawlerCategory = typeof CRAWLER_CATEGORIES[number];

export const NOT_CRAWLER_SCOPED = '';

export type IncidentCrawlerCategory = CrawlerCategory | typeof NOT_CRAWLER_SCOPED;

// The crawlers Diagnostics raises incidents for, offered as the crawler filter's choices.
export const COVERED_CRAWLERS = [
  'googlebot',
  'bingbot',
  'applebot',
  'gptbot',
  'chatgpt',
  'claudebot',
  'anthropic-ai',
  'perplexitybot',
  'amazonbot',
  'meta',
  'linkedinbot',
] as const;

export type CoveredCrawler = typeof COVERED_CRAWLERS[number];

export type CoveredCrawlerCategory = Exclude<CrawlerCategory, 'other'>;

export type CrawlerProvider =
  | 'Google'
  | 'Bing'
  | 'Apple'
  | 'OpenAI'
  | 'Anthropic'
  | 'Perplexity'
  | 'Amazon'
  | 'LinkedIn'
  | 'Meta';

export type CrawlerClassification = {
  readonly category: CoveredCrawlerCategory;
  readonly provider: CrawlerProvider;
};

export const CRAWLER_CLASSIFICATIONS: Readonly<Record<CoveredCrawler, CrawlerClassification>> = {
  googlebot: { category: 'search', provider: 'Google' },
  bingbot: { category: 'search', provider: 'Bing' },
  applebot: { category: 'search', provider: 'Apple' },
  gptbot: { category: 'ai', provider: 'OpenAI' },
  chatgpt: { category: 'ai', provider: 'OpenAI' },
  claudebot: { category: 'ai', provider: 'Anthropic' },
  'anthropic-ai': { category: 'ai', provider: 'Anthropic' },
  perplexitybot: { category: 'ai', provider: 'Perplexity' },
  amazonbot: { category: 'ai', provider: 'Amazon' },
  linkedinbot: { category: 'social', provider: 'LinkedIn' },
  meta: { category: 'social', provider: 'Meta' },
};

export const PLAN_SIZE_TIERS = ['starter', 'growth', 'pro', 'enterprise'] as const;

export type PlanSizeTier = typeof PLAN_SIZE_TIERS[number];

export const INCIDENT_LIST_SORT_KEYS = [
  'severity',
  'status',
  'incidentType',
  'accountId',
  'accountName',
  'plan',
  'firstDetectedAt',
  'lastCheckedAt',
] as const;

export type IncidentListSortKey = typeof INCIDENT_LIST_SORT_KEYS[number];

export const SORT_DIRECTIONS = ['asc', 'desc'] as const;

export type SortDirection = typeof SORT_DIRECTIONS[number];

export type AccountContext = {
  name: string | null;
  plan: string | null;
  planName: string | null;
};

export type SeverityCounts = {
  critical: number;
  high: number;
  medium: number;
  low: number;
};

export type IncidentListItem = {
  reference: string;
  userId: number;
  crawlerCategory: IncidentCrawlerCategory;
  crawlers: readonly string[];
  incidentType: string;
  status: IncidentStatus;
  severity: Severity;
  headlineDomain: string;
  otherDomainCount: number;
  affectedDomainCount: number;
  headlineDomainCount: number;
  domainCount: number;
  metricLabel: string;
  metricValue: number;
  affectedUrlCount: number;
  crawledUrlCount: number;
  requestImpactCount: number;
  triggerValue: number;
  triggerDomainCount: number;
  firstDetectedAt: string;
  account: AccountContext | null;
  lastCheckedAt: string | null;
};

export type IncidentListResponse = {
  items: readonly IncidentListItem[];
  totalCount: number;
};

export type CountersResponse = {
  flaggedAccounts: number;
  openIncidents: number;
  openBySeverity: SeverityCounts;
  lastRunAt: string | null;
  lastRunWatermark: string | null;
  nextRunAfter: string | null;
};

export type CompletedRunResponse = {
  outcome: 'completed';
  watermark: string;
  windowStart: string;
  windowEnd: string;
  eligible: number;
  detections: number;
  created: number;
  updated: number;
  skippedChecks: number;
  closedNotPaying: number;
  failedDetectors: string[];
};

export type NoNewDataRunResponse = {
  outcome: 'no-new-data';
  watermark: string | null;
  closedNotPaying: number;
  failedDetectors: string[];
};

export type RecentlyRanRunResponse = {
  outcome: 'recently-ran';
  lastWatermark: string;
  nextRunAfter: string;
  closedNotPaying: number;
  failedDetectors: string[];
};

export type RunDiagnosticsResponse = CompletedRunResponse | NoNewDataRunResponse | RecentlyRanRunResponse;

export type RunAttemptStatus = 'running' | 'completed' | 'no-new-data' | 'recently-ran' | 'failed';

export type StartDetectionRunRequest = {
  force?: boolean;
};

export type RunAttemptResponse = {
  readonly reference: string;
  readonly status: RunAttemptStatus;
  readonly startedBy: string;
  readonly forced: boolean;
  readonly startedAt: string;
  readonly finishedAt: string | null;
  readonly durationMs: number | null;
  readonly overrunning: boolean;
  readonly report: RunDiagnosticsResponse | null;
};

export type HealthResponse = {
  rollupWatermark: string | null;
  lagHours: number | null;
  lastRunAt: string | null;
};

export type IncidentHistoryStateAfterResponse = {
  affectedDomainCount: number;
  severity: Severity;
  status: IncidentStatus | ClosedReason;
};

export type IncidentHistoryEntryResponse = {
  occurredAt: string;
  actor: IncidentEventActor;
  runReference: string | null;
  eventType: IncidentEventType;
  domains: readonly string[] | null;
  domainCount: number | null;
  fromValue: string | null;
  toValue: string | null;
  detail: Record<string, unknown>;
  stateAfter: IncidentHistoryStateAfterResponse | null;
};

export const DOMAIN_STATES = ['affected', 'no_longer_affected', 'affected_when_closed'] as const;

export type DomainState = typeof DOMAIN_STATES[number];

export type IncidentDomainResponse = {
  domain: string;
  state: DomainState;
  noLongerAffectedSince: string | null;
  firstDetectedAt: string;
  lastDetectedAt: string;
  joinedAt: string | null;
  severity: Severity;
  metricLabel: string;
  metricValue: number;
  affectedUrlCount: number;
  crawledUrlCount: number;
  evidence: Record<string, unknown>;
  // A ready-to-run ClickHouse query listing the URLs behind the domain's count, or null
  // when the incident type has no query shape.
  affectedUrlQuery: string | null;
  crawlers: readonly DomainCrawlerResponse[];
};

export type DomainCrawlerResponse = {
  crawler: string;
  severity: Severity;
  metricLabel: string;
  metricValue: number;
  currentRequests: number | null;
  usualWeekRequests: number | null;
  firstDetectedAt: string;
  lastRequestAt: string | null;
  noLongerAffected: boolean;
};

// Identifiers, not display labels.
export type CrawlerEvidenceRowResponse = {
  crawler: string;
  severity: Severity;
  metricLabel: string;
  metricValue: number;
  worstDomain: string;
  worstMetricValue: number;
  currentRequests: number;
  usualWeekRequests: number;
  lastRequestAt: string | null;
  affectedDomainCount: number;
  totalledDomainCount: number;
  noLongerAffected: boolean;
};

export type ProviderEvidenceResponse = {
  provider: string;
  severity: Severity;
  metricValue: number;
  currentRequests: number;
  usualWeekRequests: number;
  lastRequestAt: string | null;
  crawlers: readonly CrawlerEvidenceRowResponse[];
  noLongerAffected: boolean;
};

export type IncidentDetailResponse = {
  reference: string;
  userId: number;
  crawlerCategory: IncidentCrawlerCategory;
  incidentType: string;
  status: IncidentStatus;
  closedReason: ClosedReason | null;
  severity: Severity;
  metricLabel: string;
  metricValue: number;
  affectedUrlCount: number;
  crawledUrlCount: number;
  requestImpactCount: number;
  requestTotalCount: number;
  affectedDomainCount: number;
  headlineDomain: string;
  firstDetectedAt: string;
  lastDetectedAt: string;
  lastUpdatedAt: string;
  lastCheckedAt: string | null;
  closedAt: string | null;
  account: AccountContext | null;
  history: readonly IncidentHistoryEntryResponse[];
  domains: readonly IncidentDomainResponse[];
  crawlerEvidence: readonly ProviderEvidenceResponse[];
};

export const INCIDENT_VERDICTS = ['close'] as const;

export type IncidentVerdict = typeof INCIDENT_VERDICTS[number];

export type IncidentVerdictRequest = {
  verdict: IncidentVerdict;
};

export const DIAGNOSTICS_RESET_PHRASE = 'reset diagnostics';

export const confirmsDiagnosticsReset = (typed: string): boolean =>
  typed.trim().toLowerCase() === DIAGNOSTICS_RESET_PHRASE;

export type DiagnosticsResetRequest = {
  phrase: string;
};

export type AffectedUrlRow = {
  domain: string;
  url: string;
  statusCode: number;
  lastSeen: string;
  requestCount: number;
};

export type AffectedUrlsResponse = {
  urls: readonly AffectedUrlRow[];
  limit: number;
};

export type AffectedUrlsExportResponse = {
  jobId: string;
};

export const QUERY_PURPOSES = ['detection', 'enrichment'] as const;

export type QueryPurpose = typeof QUERY_PURPOSES[number];

export const QUERY_SOURCES = ['clickhouse-domain-crawler-metrics', 'clickhouse-analytics'] as const;

export type QuerySource = typeof QUERY_SOURCES[number];

export type FixedParameterResponse = {
  readonly name: string;
  readonly kind: 'fixed';
  readonly value: number | string | readonly number[] | readonly string[];
};

export type RunTimeParameterResponse = {
  readonly name: string;
  readonly kind: 'run-time';
  readonly description: string;
};

export type QueryParameterResponse = FixedParameterResponse | RunTimeParameterResponse;

export type CatalogedQueryResponse = {
  readonly label: string;
  readonly purpose: QueryPurpose;
  readonly source: QuerySource;
  readonly sql: string;
  readonly parameters: readonly QueryParameterResponse[];
};

export const RULE_UNITS = ['days', 'requests', 'share'] as const;

export type RuleUnit = typeof RULE_UNITS[number];

export const THRESHOLD_RULE_NAMES = [
  'window',
  'eligible-from',
  'current-window',
  'baseline',
  'baseline-floor',
  'baseline-active-days',
  'while-open-low-below',
  'recovered-below',
] as const;

export type ThresholdRuleName = typeof THRESHOLD_RULE_NAMES[number];

export type ThresholdRuleResponse = {
  readonly kind: 'threshold';
  readonly name: ThresholdRuleName;
  readonly value: number;
  readonly unit: RuleUnit;
};

export type SeverityRuleResponse = {
  readonly kind: 'severity';
  readonly severity: Severity;
  readonly opensIncident: boolean;
  readonly share: number;
};

export type DetectorRuleResponse = ThresholdRuleResponse | SeverityRuleResponse;

export type CatalogedDetectorResponse = {
  readonly incidentType: string;
  readonly queries: readonly CatalogedQueryResponse[];
  readonly rules: readonly DetectorRuleResponse[];
};

export type DetectorCatalogResponse = {
  readonly detectors: readonly CatalogedDetectorResponse[];
};
